<template>
  <div class="context-screen">
    <div class="context-container">
      <!-- Header Section -->
      <header class="context-header mb-4">
        <h1 class="welcome-text">Welcome, {{ customerName }}</h1>
        <p class="instruction-text">Choose how you'd like to continue</p>
      </header>

      <!-- Company / Customer contexts (no company name shown) -->
      <section v-if="companyContexts.length" class="mb-8">
        <p class="section-title">CONTINUE AS</p>
        <div class="context-list">
          <div v-for="item in companyContexts" :key="item.context_id" class="context-card-wrapper"
            @click="handleSelect(item.key)">
            <v-card class="context-card" :class="{ 'context-card--active': selectedContextId === item.context_id }"
              flat>
              <div class="context-card__inner">
                <div class="context-card__icon-wrap">
                  <v-avatar color="primary" variant="tonal" size="40" rounded="lg">
                    <v-icon color="primary" :icon="getRoleIcon(item.type)" />
                  </v-avatar>

                </div>
                <div class="context-card__content">
                  <!-- show role/label instead of company name -->
                  <h2 class="context-card__title text-capitalize">{{ item.type }}</h2>
                </div>
              </div>
            </v-card>
          </div>
        </div>
      </section>

      <!-- Vendor contexts -->
      <section v-if="vendorContexts.length" class="mb-4">
        <p class="section-title">ASSOCIATED VENDORS</p>
        <div class="context-list">
          <div v-for="item in vendorContexts" :key="item.context_id" class="context-card-wrapper"
            @click="handleSelect(item.key)">
            <v-card class="context-card" :class="{ 'context-card--active': selectedContextId === item.context_id }"
              flat>
              <div class="context-card__inner">
                <v-avatar variant="tonal" size="40" rounded="lg" color="primary">
                  <img v-if="resolveContextLogo(item)" :src="resolveContextLogo(item)" alt="Context logo"
                    class="context-logo" loading="lazy" />
                  <v-icon v-else :icon="getRoleIcon(item.type)" size="24" />
                </v-avatar>
                <div class="context-card__content">
                  <!-- show vendor/company name for vendor contexts -->
                  <h3 class="context-card__title">{{ item.vendor?.name || item.company?.name || item.label || item.type
                    }}</h3>
                  <span class="context-card__subtitle" v-if="item.roles && item.roles.length">
                    <template v-if="item.roles.length === 1">
                      <v-chip color="primary" label size="small">{{ item.roles[0] }}</v-chip>
                    </template>
                    <template v-else>
                      <v-chip color="primary" label size="small">{{ item.roles[0] }}</v-chip>
                      <span class="text-primary"> +{{ item.roles.length - 1 }} roles</span>
                    </template>
                  </span>
                </div>
              </div>
            </v-card>
          </div>
        </div>
      </section>

      <!-- Error Message -->
      <Transition name="fade">
        <p v-if="formError" class="form-error">{{ formError }}</p>
      </Transition>

      <!-- Optional: Loading Overlay -->
      <v-overlay v-model="isSubmitting" class="align-center justify-center" persistent>
        <v-progress-circular color="primary" indeterminate size="64" />
      </v-overlay>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAppStore } from "@/shared/stores/app";
import {
  selectContext,
  switchContext,
  selectVendorContext,
  saveAuthToken,
  saveActiveContext,
  getContextKind,
  clearPendingContextSelection,
  clearContextStorage,
  type LoginCustomer,
  type LoginContext,
  fetchProfile,
  hasProfileEnvelope,
} from "@/modules/auth/services/auth.api";
import {
  clearCustomer,
  getContexts,
  getCustomer,
  getProfileLastSynced,
  getSelectionToken,
  setCustomer,
  setProfileLastSynced,
} from "@/modules/auth/services/auth.storage";
import { isVendorMode } from "@/shared/utils/appMode";

const router = useRouter();

const isSubmitting = ref(false);
const formError = ref("");
const selectedContextId = ref("");
function syncFromStorage() {
  contexts.value = getContexts() || [];
  customer.value = (getCustomer<LoginCustomer>() as any) || null;
}

const contexts = ref<LoginContext[]>(getContexts() || []);

const companyContexts = computed<LoginContext[]>(() => {
  return contexts.value.filter((c) => {
    if (!c) return false;
    if (c.type === "company" || c.type === "customer") return true;
    const roles = c.roles || [];
    return roles.some((r) => String(r).toLowerCase().includes("company"));
  });
});

const vendorContexts = computed<LoginContext[]>(() => {
  return contexts.value.filter((c) => {
    if (!c) return false;
    if (c.type === "vendor") return true;
    const roles = c.roles || [];
    return roles.some((r) => {
      const rr = String(r).toLowerCase();
      return rr.includes("vendor") || rr.includes("service_provider") || rr.includes("vendor_");
    });
  });
});

const customer = ref<LoginCustomer | null>((getCustomer<LoginCustomer>() as any) || null);

const customerName = computed(() => customer.value?.first_name || "User");

onMounted(async () => {
  window.addEventListener("auth-changed", syncFromStorage);

  // try to refresh profile on page load (best-effort)
  await fetchProfileAndSync();

  // if no contexts available after sync, redirect to login
  if ((contexts.value || []).length === 0) {
    clearPendingContextSelection();
    router.replace({ name: "login.page" });
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("auth-changed", syncFromStorage);
});

/**
 * Role-based Visual Mapping
 */
function getRoleIcon(role: string): string {
  const r = (role || "").toString().toLowerCase();
  const mapping: Record<string, string> = {
    admin: "mdi-office-building-cog-outline",
    staff: "mdi-office-building-outline",
    company: "mdi-office-building-outline",
    vendor: "mdi-storefront",
    customer: "mdi-account-outline",
    service_provider: "mdi-briefcase-variant-outline",
  };
  return mapping[r] || "mdi-account-circle-outline";
}


function resolveContextLogo(item: LoginContext) {
  if (!item || typeof item !== "object") return "";
  if (item.type === "company") return String(item.company?.logo || "").trim();
  if (item.type === "vendor") return String(item.vendor?.logo || "").trim();
  return "";
}


/**
 * Selection Logic
 */
async function handleSelect(contextId: string) {
  selectedContextId.value = contextId;
  await handleSubmit();
}

async function fetchProfileAndSync() {
  try {
    // check last sync timestamp (ms). If synced less than 1 minute ago, skip fetch
    const last = getProfileLastSynced();
    const now = Date.now();
    if (last && now - last < 60_000) {
      // recent sync exists, skip network call
      return;
    }

    const data = await fetchProfile(); // uses auth.api.ts
    const profile = hasProfileEnvelope(data) ? data.data : (data as any);

    if (profile) {
      // prefer nested client object if present (client.first_name)
      const client = (profile && (profile.client || profile.user || profile)) as any;

      // Preserve any stored `first_login` flag (profile payloads may omit it).
      let storedFirstLogin: boolean | undefined = undefined;
      try {
        const existing = (getCustomer<any>() as any) || null;
        if (typeof existing?.first_login === "boolean") storedFirstLogin = existing.first_login;
      } catch {
        // ignore parse errors
      }

      const customerData: Partial<LoginCustomer> = {
        id: client?.id ?? client?.user_id ?? profile?.id ?? 0,
        first_name: client?.first_name ?? profile?.first_name ?? client?.fname ?? client?.name ?? "",
        last_name: client?.last_name ?? profile?.last_name ?? client?.lname ?? client?.name ?? "",
        email: client?.email ?? profile?.email ?? "",
        phone: client?.phone ?? profile?.phone ?? "",
        avatar: client?.avatar ?? profile?.avatar ?? "",
      };

      const customerForStorage: any = { ...customerData };
      const resolvedFirstLogin =
        typeof client?.first_login === "boolean"
          ? client.first_login
          : typeof storedFirstLogin === "boolean"
            ? storedFirstLogin
            : undefined;
      if (typeof resolvedFirstLogin === "boolean") {
        customerForStorage.first_login = resolvedFirstLogin;
      }
      setCustomer(customerForStorage);
      customer.value = customerForStorage as any;
      window.dispatchEvent(new Event("auth-changed"));

      // update last synced timestamp
      setProfileLastSynced(Date.now());
    }

    contexts.value = getContexts() || [];
  } catch (err) {
    // clear all related localStorage entries on error (best-effort)
    try {
      clearCustomer();
      clearPendingContextSelection();
      clearContextStorage();
    } catch {
      // ignore storage errors
    } finally {
      // notify app that auth/context changed
      window.dispatchEvent(new Event("auth-changed"));
    }
    // swallow error (best-effort)
  }
}

async function handleSubmit(){
  if(isVendorMode()) {
    await selectVendorConext(selectedContextId.value);
  } else {
    await handleDefaultSubmit();
  }
}
async function selectVendorConext(contextId: string) {
  try {
    const resp = await selectVendorContext({ context_id: contextId });
    const token = resp?.token || resp?.data?.token;
    const selectedContext =
      contexts.value.find((c) => c.context_id === contextId || c.key === contextId) || null;
    const activeContext = {
      ...(selectedContext || {}),
      ...(resp?.active_context || resp?.data?.active_context || {}),
      context_id: contextId,
    };

    saveAuthToken(token);
    saveActiveContext(activeContext);
    await fetchProfileAndSync();

    const profileSetup = (activeContext as any)?.profile_setup || null;
    if (profileSetup?.setup_required) {
      router.push({ name: "vendor.profile-setup" });
      return;
    }

    router.push({ name: "vendor.dashboard" });
  } catch (err) {
    // ignore errors (best-effort)
  }
}

async function handleDefaultSubmit() {


  if (!selectedContextId.value) return;

  formError.value = "";
  isSubmitting.value = true;

  try {
    const hasSelectionToken = Boolean(getSelectionToken());

    let response;
    if (hasSelectionToken) {
      response = await selectContext({ context_id: selectedContextId.value, is_vendor: false });
    } else {
      response = await switchContext({ context_id: selectedContextId.value });
    }

    const token = response?.data?.token;


    if (token) {
      saveAuthToken(token);
    }


    clearPendingContextSelection();

    const selectedContext =
      contexts.value.find((c) => c.context_id === selectedContextId.value) || null;
    const contextFromResponse = (response?.active_context || response?.data?.active_context || null) as
      | Partial<LoginContext>
      | null;
    const mergedContext = {
      ...(selectedContext || {}),
      ...(contextFromResponse || {}),
      context_id: selectedContextId.value,
    };

    saveActiveContext(mergedContext);

    // fetch latest profile and sync localStorage (best-effort)
    await fetchProfileAndSync();

    const profileSetup = (mergedContext as any)?.profile_setup || null;
    const setupCompleted = profileSetup?.setup_completed;
    const firstLogin = (mergedContext as any)?.first_login === true;

    // Vendor profile setup should only run when setup is NOT completed
    if (setupCompleted === false) {
      router.push({ name: "vendor.profile-setup" });
      return;
    }

    // If setup is completed (or profile_setup missing) but it's the user's first login,
    // force password change instead of taking them to profile setup.
    if (firstLogin) {
      router.push({ name: "change-password.page" });
      return;
    }

    const kind = getContextKind(mergedContext);
    let targetRoute = { name: "marketplace.home" };
    if (kind === "customer") {
      targetRoute = { name: "customer.dashboard" };
    } else if (kind === "vendor") {
      targetRoute = { name: "vendor.dashboard" };
    }

    router.push(targetRoute);


  } catch (error: any) {
    formError.value =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      "Unable to select context. Please try again.";

    // Remove local storage related to contexts on error
    clearContextStorage();
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<style scoped lang="scss">
.context-screen {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px;
}

.context-container {
  width: 100%;
  max-width: 440px;
}

.context-header {
  text-align: center;
  // animation: slideDown 0.5s ease-out;

  .welcome-text {
    font-size: 28px;
    font-weight: 800;
    color: #1e293b;
    margin-bottom: 8px;
    letter-spacing: -0.5px;
  }

  .instruction-text {
    font-size: 16px;
    color: #64748b;
    font-weight: 500;
  }
}

.context-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.context-card-wrapper {
  perspective: 1000px;
  cursor: pointer;
}

.context-card {
  border: 1.5px solid #e2e8f0 !important;
  border-radius: 16px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: white;


  &__inner {
    padding: 8px 10px;
    display: flex;
    align-items: center;
    gap: 16px;
  }

  &__icon-wrap {
    width: 52px;
    height: 52px;
    min-width: 52px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.3s ease;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 2px;
    overflow: hidden;
  }

  &__title {
    font-size: 17px;
    font-weight: 700;
    color: #0f172a;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  &__subtitle {
    font-size: 14px;
    font-weight: 500;
    color: #64748b;
  }
}

.context-logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.form-error {
  color: #ef4444;
  font-size: 14px;
  text-align: center;
  font-weight: 500;
  background: #fef2f2;
  padding: 10px;
  border-radius: 8px;
  margin: 0;
}

/* Animations */
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.section-title {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
  color: #0f172a;
}
</style>
